<template>
  <div
    class="addition-form"
    @keyup.enter="notifyChanged()"
  >
    <div
      v-if="withBusinessName"
      class="addition-form-row"
    >
      <div class="addition-form-row__column">
        <AkInput
          id="business_name"
          v-model="business_name"
          :label="$t('Business name')"
          :disabled="disabled"
          :errors="soleTraderDetailsErrors['business.name']"
          type="text"
        />
      </div>
    </div>
    <div class="addition-form-row">
      <AkParagraph
        class="addition-form__description"
        size="sm"
      >
        <i18n-t
          keypath="Please enter information about the {0} of the company"
          class="addition-form-row__column form-group"
        >
          <strong>{{ $t('owner') }}</strong>
        </i18n-t>
      </AkParagraph>
    </div>
    <div class="addition-form-row">
      <div class="addition-form-row__column addition-form-row__column--twice">
        <AkInput
          id="address_first_name"
          v-model="first_name"
          :label="$t('First name')"
          :disabled="disabled"
          :errors="soleTraderDetailsErrors['owner_address.name.first_name']"
          type="text"
        />
      </div>
      <div class="addition-form-row__column addition-form-row__column--twice">
        <AkInput
          id="address_last_name"
          v-model="last_name"
          :label="$t('Last name')"
          :disabled="disabled"
          :errors="soleTraderDetailsErrors['owner_address.name.last_name']"
          type="text"
        />
      </div>
    </div>
    <div class="addition-form-row">
      <div class="addition-form-row__column">
        <AkInput
          id="birthdate"
          v-model="owner_birthdate"
          v-mask="'##/##/####'"
          :label="$t('Birth date')"
          :disabled="disabled"
          :errors="soleTraderDetailsErrors.owner_birthdate"
          type="text"
        />
      </div>
    </div>
    <div class="addition-form-row">
      <AkParagraph
        size="sm"
        class="addition-form__description"
      >
        <i18n-t
          keypath="Please enter the owner's {0} address"
          tag="div"
        >
          <strong>{{ $t('residential') }}</strong>
        </i18n-t>
      </AkParagraph>
    </div>
    <div class="addition-form-row">
      <div class="addition-form-row__column">
        <AkInput
          id="address_street"
          ref="streetInput"
          v-model="street"
          :label="$t('Street')"
          :disabled="disabled"
          :errors="soleTraderDetailsErrors['owner_address.street']"
          type="text"
        />
      </div>
    </div>
    <div class="addition-form-row">
      <div class="addition-form-row__column addition-form-row__column--twice">
        <AkInput
          id="address_postal_code"
          v-model="postal_code"
          :label="$t('Postal code')"
          :disabled="disabled"
          :errors="soleTraderDetailsErrors['owner_address.postal_code']"
          type="text"
        />
      </div>
      <div class="addition-form-row__column addition-form-row__column--twice">
        <AkInput
          id="address_city"
          v-model="city"
          :label="$t('City')"
          :disabled="disabled"
          :errors="soleTraderDetailsErrors['owner_address.city']"
          type="text"
        />
      </div>
    </div>
    <div class="addition-form-row">
      <div
        v-if="getFormattedCountries"
        class="addition-form-row__column"
      >
        <AkAutocomplete
          v-model="country_id"
          :options="countries"
          :label="$t('Country')"
          data-testid="countrySelector"
          :disabled="disabled"
          :reset-btn-visible="false"
          :errors="soleTraderDetailsErrors['owner_address.country_id']"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { mapActions, mapGetters, mapState } from 'vuex';
import { isEnabled, FeatureFlag } from '@monolith/legacy/services/features';
import { AsyncScript, initScript } from '@monolith/legacy/modules/shared/services/load-async-script';
import { defineComponent } from 'vue';
import { toAddress } from '@monolith/legacy/modules/shared/utils/tooling';
import { resolveAkInputElement } from '@monolith/legacy/modules/shared/utils/ds-input';
import { CountryLight } from '@core/types/country';
import { AkInput, AkAutocomplete } from '@ankorstore/design-system';
export default defineComponent({
  name: 'FormSoleTrader',
  components: { AkInput, AkAutocomplete },
  props: {
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    withBusinessName: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['changed'],
  data() {
    return {
      formattedCountries: [],
      autocomplete: null,
      autocompleteListener: null,
    };
  },
  computed: {
    ...mapGetters('global/countries', ['getOrderedNoTopCountries', 'getOrderedTopCountries']),
    ...mapGetters('account/soleTrader', ['soleTraderDetailsErrors', 'soleTrader']),
    // `countries` is already taken by the AkAutocomplete options list below, hence the rename.
    ...mapState('global/countries', { topCountriesOrder: 'topCountriesOrder', countriesByIsoCode: 'countries' }),
    owner_birthdate: {
      get: function () {
        return this.soleTrader?.owner_birthdate;
      },
      set: function (value) {
        this.setSoleTraderDetail({ owner_birthdate: value });
      },
    },
    business_name: {
      get: function () {
        return this.soleTrader?.business?.name;
      },
      set: function (value) {
        this.setSoleTraderDetail({ business_name: value });
      },
    },
    first_name: {
      get: function () {
        return this.soleTrader?.owner_address?.first_name;
      },
      set: function (value) {
        this.setSoleTraderDetail({ first_name: value });
      },
    },
    last_name: {
      get: function () {
        return this.soleTrader?.owner_address?.last_name;
      },
      set: function (value) {
        this.setSoleTraderDetail({ last_name: value });
      },
    },
    street: {
      get: function () {
        return this.soleTrader?.owner_address?.street;
      },
      set: function (value) {
        this.setSoleTraderDetail({ street: value });
      },
    },
    postal_code: {
      get: function () {
        return this.soleTrader?.owner_address?.postal_code;
      },
      set: function (value) {
        this.setSoleTraderDetail({ postal_code: value });
      },
    },
    city: {
      get: function () {
        return this.soleTrader?.owner_address?.city;
      },
      set: function (value) {
        this.setSoleTraderDetail({ city: value });
      },
    },
    country_id: {
      get: function () {
        return this.soleTrader?.owner_address?.country_id;
      },
      set: function (value) {
        this.setSoleTraderDetail({ country_id: value });
      },
    },
    getFormattedCountries(): CountryLight[][] {
      return [
        [
          ...this.getOrderedTopCountries.map(({ id, name }) => ({
            label: name,
            value: id,
          })),
        ],
        [
          ...this.getOrderedNoTopCountries.map(({ id, name }) => ({
            label: name,
            value: id,
          })),
        ],
      ];
    },
    countries(): CountryLight[] {
      return this.getOrderedTopCountries.concat(this.getOrderedNoTopCountries).map(({ id, name }) => ({
        label: name,
        value: id,
      }));
    },
  },
  watch: {
    topCountriesOrder(countries): void {
      if (!isEnabled(FeatureFlag.RegisterGeodataVerification)) {
        return;
      }

      initScript(AsyncScript.GOOGLE_MAP, () => {
        if (this.autocomplete) {
          this.autocomplete.setComponentRestrictions({ country: countries });
          return;
        }

        const streetInput = this.getStreetInput();
        if (!streetInput) {
          return;
        }

        this.autocomplete = new google.maps.places.Autocomplete(streetInput, {
          componentRestrictions: { country: countries },
          fields: ['address_components', 'geometry'],
          types: ['address'],
        });

        this.autocompleteListener = this.autocomplete.addListener('place_changed', this.fillInAddress);
      });
    },
  },
  created(): void {
    this.$store.dispatch('global/countries/fetchCountries');
  },
  beforeUnmount(): void {
    const mapsEvent = window.google?.maps?.event;
    if (this.autocompleteListener) {
      mapsEvent?.removeListener(this.autocompleteListener);
      this.autocompleteListener = null;
    }
    if (this.autocomplete) {
      mapsEvent?.clearInstanceListeners(this.autocomplete);
      this.autocomplete = null;
    }
  },
  methods: {
    ...mapActions('account/soleTrader', ['setSoleTraderDetail']),
    getStreetInput(): HTMLInputElement | null {
      return resolveAkInputElement(this.$refs.streetInput);
    },
    notifyChanged(): void {
      this.$emit('changed');
    },
    fillInAddress(): void {
      const place = this.autocomplete.getPlace();
      // Places fires `place_changed` for Enter over free text as well as for a real pick, handing
      // back a bare `{ name }` for the former and nothing at all when no prediction was ever
      // resolved. `toAddress` reads `place.address_components` straight off it, so an absent place
      // throws inside the SDK's own listener and kills the autocomplete for the rest of the page.
      // The street, postcode, city and country fields are all on screen here, so unlike
      // `autocomplete-address.vue` a partial pick stays visible to the user and the per-field
      // guards below are kept.
      if (!place?.address_components?.length) {
        return;
      }

      const address = toAddress(place, (countryShortName) => {
        return this.countriesByIsoCode[countryShortName];
      });

      if (address.street) {
        this.street = address.street;
      }
      if (address.postal_code) {
        this.postal_code = address.postal_code;
      }
      if (address.city) {
        this.city = address.city;
      }
      if (address.country?.id) {
        this.country_id = address.country.id;
      }

      const streetInput = this.getStreetInput();
      if (streetInput) {
        streetInput.value = this.street ?? '';
      }
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@css/vue-import';

.form-group {
  flex-direction: column;
}

.addition-form {
  .addition-form-row {
    @apply ds-mb-5;
  }
  &__description {
    @apply ds-text-neutral-900;
  }
}

.addition-form-row {
  @apply ds-gap-4 lg:ds-flex;
  &__column {
    @apply ds-w-full;
    &--twice {
      @apply ds-w-full lg:ds-w-1/2;
    }
  }
}
</style>
